import { ReactElement, useCallback, useEffect, useState } from 'react';
import { UIButton, UIExternalLinkButton, UISubNav, useUINav } from '@/ui';
import { DevicesList, ActivateDeviceModal } from '@/devices/components';
import { useCompanyContext } from '@/companies/providers';
import { useDevices } from '@/devices/providers';
import { Navigate } from 'react-router';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';

export function DevicesSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            {
                text: t('shared.labels.settings')
            },
            { text: t('shared.labels.devices') }
        ]);
    }, [setParts, t]);

    const { company } = useCompanyContext();
    const { devices } = useDevices(company.id);
    const { canViewDevices, canEditDevices } = useCompanyPermissions();

    const [isActivateOpen, setIsActivateOpen] = useState(false);

    // Activation requires an active Lone Worker Device subscription; without one
    // there are no devices to activate, so only offer "Buy Devices".
    const canActivate = canEditDevices && company.lwdSubscription !== null;

    const subtitle = t('pages.devices_settings.count', {
        count: devices.length
    });

    const rightNode = useCallback(
        () => (
            <div style={{ display: 'flex', gap: 12 }}>
                <UIExternalLinkButton
                    href="https://www.safepointapp.com/hardware/lone-worker-device-with-sim"
                    variant={canActivate ? 'default' : 'primary'}
                    text={t('pages.devices_settings.buy')}
                    leftIcon={'shopping-cart'}
                />
                {canActivate && (
                    <UIButton
                        variant="primary"
                        text={t('pages.devices_settings.activate')}
                        leftIcon={'zap'}
                        onClick={() => setIsActivateOpen(true)}
                    />
                )}
            </div>
        ),
        [canActivate, t]
    );

    if (!canViewDevices) {
        return <Navigate to="/" replace />;
    }

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                padding: 32,
                gap: 24
            }}
        >
            <UISubNav
                title={t('shared.labels.devices')}
                subTitle={subtitle}
                rightItems={devices.length > 0 ? rightNode() : undefined}
            />
            <DevicesList
                companyId={company.id}
                onActivateDevice={
                    canActivate ? () => setIsActivateOpen(true) : undefined
                }
            />
            <ActivateDeviceModal
                companyId={company.id}
                isOpen={isActivateOpen}
                onClose={() => setIsActivateOpen(false)}
            />
        </div>
    );
}
